import { Box, Divider, Typography } from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import { useTranslation } from 'react-i18next';
import { formatTime } from '../../utils';
import { ActivityRecording } from './ActivityTile';
import { useEffect, useRef } from 'react';
import { useActivityLicensePlate } from '../../api/queries/live/activityLicensePlate';

interface ActivityOverviewProps {
  jobsiteId: string;
  recordings: ActivityRecording[];
  licensePlates: Record<string, string>;
  activeRecording: string;
  onSelectRecording: (id: string) => void;
}

export default function ActivityOverview({
  jobsiteId,
  recordings,
  licensePlates,
  activeRecording,
  onSelectRecording,
}: ActivityOverviewProps) {
  const { t } = useTranslation();
  const recordingRefs = useRef<Map<string, any>>(new Map());

  const numberOfRecordings = recordings.length;

  useEffect(() => {
    if (activeRecording) {
      const activeElement = recordingRefs.current.get(activeRecording);
      if (activeElement) {
        activeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [activeRecording]);

  const handleSelectRecording = (id: string) => {
    onSelectRecording(id);
  };

  const timeLineItems = recordings.map((recording) => ({
    id: recording.id,
    date: recording.captureStartAt,
    licensePlate: recording.licensePlate || licensePlates[recording.captureStartAt],
    licensePlateStatus: recording.licensePlateStatus,
  }));

  return (
    <Box
      sx={{
        borderRadius: '8px',
        padding: '1em',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        gap: '0.5em',
        overflowY: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', gap: '0.5em', alignItems: 'baseline' }}>
        <Typography variant='h6'>{t('activity.activitiesToday')}</Typography>
        <Typography variant='body2'>– {numberOfRecordings}</Typography>
      </Box>
      <Divider />
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
          marginBlockStart: 0,
          paddingInlineStart: 0,
        }}
      >
        {timeLineItems.map((recording, index) => (
          <TimelineItem
            key={recording.id}
            ref={(el) => {
              if (el) {
                recordingRefs.current.set(recording.id, el);
              } else {
                recordingRefs.current.delete(recording.id);
              }
            }}
          >
            <TimelineSeparator>
              <TimelineDot sx={activeRecording === recording.id ? { backgroundColor: '#596df6' } : undefined} />
              {index < recordings.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent>
              <ActivityItem
                key={recording.id}
                id={recording.id}
                jobsiteId={jobsiteId}
                date={new Date(recording.date)}
                captureStartAt={recording.date}
                licensePlate={recording.licensePlate}
                licensePlateStatus={recording.licensePlateStatus}
                onSelectActivity={handleSelectRecording}
              />
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    </Box>
  );
}

interface ActivityItemProps {
  id: string;
  jobsiteId: string;
  date: Date;
  captureStartAt: string;
  licensePlate: string | undefined;
  licensePlateStatus: string;
  onSelectActivity: (id: string) => void;
}

function ActivityItem({
  id,
  jobsiteId,
  date,
  captureStartAt,
  licensePlate,
  licensePlateStatus,
  onSelectActivity,
}: ActivityItemProps) {
  const licensePlateQuery = useActivityLicensePlate({
    jobsiteId,
    captureStartAt,
    enabled: licensePlateStatus === 'finished' && !licensePlate,
  });

  const displayLicensePlate = licensePlate || licensePlateQuery.data || '—';

  const formattedTime = formatTime(date);
  return (
    <Box
      onClick={() => onSelectActivity(id)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0',
        padding: '0 0.5em',
        cursor: 'pointer',
        borderRadius: 2,
        marginBottom: 2,
        boxSizing: 'border-box',
        '&:hover': {
          backgroundColor: '#f8fafc',
        },
      }}
    >
      <Typography variant='h6'>{formattedTime}</Typography>
      <Typography variant='body2'>{displayLicensePlate}</Typography>
    </Box>
  );
}
